// src/components/KbActPanel.tsx (2026-07-01 新設・ベータ / 2026-07-18 全面改修 / 2026-09-07 期間指定を入院実績と統一)
// 行為別売上分析。fact_kb_act_long v3 (046 を母集団に 037 の行為列を pivot・患者ごとの残差は「調整」で自科へ。
//   科×月×医療区分×予定緊急 の全粒度で 入院実績タブの入院収入 と 1 円まで一致・build 時に突合ゲートあり)。
// 構成: ①主要4指標カード (期間の月平均 + 前年度同期間比 + 年度12ヶ月の 今年度/前年度 折れ線・クリックで指標切替)
//       ②月次推移の縦積み上げ棒 (直近13ヶ月=前年同月〜当月・x=年月・y=実額・セグメント=金額+構成比%・期間の月は帯で強調)
//         [2026-09-07 ユーザ指示] 棒グラフだけは年度枠でなく直近13ヶ月 (カードの折れ線と科別表は年度 4月〜3月のまま)
//       ③診療科×年月マトリクス (年度12ヶ月 + 期間の月平均・値=表示指標・期間の月は枠で強調・行クリックで科フォーカス=グラフ連動)
// 期間 = サイドバーの 開始月/終了月 (入院実績(前年度・予算比較) と同じコントロール・同じ store の periodFrom/To)。
//   カードの値と表の「月平均」列はその期間の月平均 (データのある月だけが分母)。年度は最新確定月 (dateMax) の年度に固定。
// データは 前年度4月〜今年度3月 の 24 ヶ月を取得 (前年度線・前年比をクライアントで計算)。
// 印刷: A4横2枚 (1枚目=条件ヘッダ+タイトル+カード+グラフ / 2枚目=条件ヘッダ+表)。styles.css の .kbact-* 参照。
//   [2026-09-07 ユーザ指示] 入院実績と同じく、各印刷ページ先頭に ContextBar の印刷専用コピー (条件) と
//   タイトル行 (対象・期間の月平均・年度・表示指標・収入の基準・凡例) を付ける (.budget-print-page で改ページ)。
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Plot from "@observablehq/plot";
import { PlotFigure } from "./Plot";
import { ContextBar } from "./ContextBar";
import { api } from "../lib/api";
import { useFilters, monthShift } from "../store";
import { buildMonthAxisMark } from "../lib/chartAxis";
import { fiscalYearOf, fyDate, fyMonthDates, monthLabel, selectedMonthDates, selectedRangeLabel } from "../lib/fiscalYear";
import { useSort, SortTh } from "../lib/useSort";
import type { KbActResponse, MetricRow } from "@shared/api";
import { IS_THETA } from "../lib/gpMode";

// 行為区分 → 3カテゴリ。[2026-08-17] 入院基本料 + 特定入院料 を「入院料」に統合。
// [2026-07-23] 手術 = 手術/手術材料/手術薬剤
// (輸血・麻酔はその他。serverで020手術小計から付替え)。
//   輸血・麻酔 (+明細再現残差) は「その他」へ。手術売上タブの手術総金額と「手術」が構成的に一致。
// [2026-09-07] 初再診・在宅・労災・調整 (fact v3 で患者の科に付くようになった分) も「その他」。
const CAT_OF: Record<string, string> = { 入院基本料: "入院料", 特定入院料: "入院料", 手術: "手術" };
const catOf = (k: string) => CAT_OF[k] ?? "その他";
const CAT_ORDER = ["入院料", "手術", "その他"];
const CAT_COLORS: Record<string, string> = { 入院料: "#386FA4", "手術": "#D9A036", その他: "#94a3b8" };
// [2026-08-22 ユーザ指示] 「収入の基準」が控除後のときは、総額カード・見出し・CSV名を
//   実体どおり **薬剤材料費差引後入院収入** と名乗る (fact_long_all の同名指標と同値。
//   2026-08-22 に控除の源を EF の包括前へ揃えたので、金額もその指標と一致する)。
//   ★この文字列に「粗利」を入れないこと — θ版ビルドの機械置換 (粗利→GP) を通っても
//     同じ表記になるようにするため。
const displayLabel = (value: string, gross = false) =>
  value === "手術" ? "手術(輸血除く)"
  : (gross && value === "入院収入") ? "薬剤材料費差引後入院収入"
  : value;

// 表示指標 → 対象カテゴリ (入院収入 = 全カテゴリ)
const METRIC_CATS: Record<string, string[]> = {
  入院収入: CAT_ORDER,
  入院料: ["入院料"],
  "手術": ["手術"],
  その他: ["その他"],
};

const sen = (v: number | null) => v == null ? "—" : Math.round(v / 1000).toLocaleString();
const ymShort = (m: string) => `${m.slice(2, 4)}年${m.slice(5, 7)}月`;
// 金額単位の自動切替 (値ごと): 1 億以上 → 億 (小数2桁) / 未満 → 万 (整数)
// [2026-08-21 ユーザ指示] 億は小数2位まで (1桁だと 2.04/2.11 億が同じ「2.0億」に丸まる)
const fmtAmt = (v: number) => v >= 1e8
  ? `${(v / 1e8).toFixed(2)}億`
  : `${Math.round(v / 1e4).toLocaleString()}万`;
const CHART_H = 224;   // h-56 (px) — セグメント内テキストの表示判定に使う

type MonthCat = Record<string, Record<string, number>>;   // month → cat → 円

export function KbActPanel({ dateMax, metrics, builtAt }: { dateMax: string; metrics: MetricRow[]; builtAt: string }) {
  const f = useFilters();
  const metric = f.kbActMetric;
  const cats = METRIC_CATS[metric] ?? CAT_ORDER;
  const stacked = cats.length > 1;   // 入院収入 = 積み上げ / 個別指標 = 単色
  const iryo  = f.iryo  === "合計" ? null : f.iryo;
  const yotei = f.yotei === "合計" ? null : f.yotei;
  const dept = f.focusedDept;   // 科フォーカス (表行クリック / サイドバーと連動)

  // [2026-09-07] 年度枠 + 期間 (開始月〜終了月) = 入院実績(前年度・予算比較) と同じ解釈
  const fy = fiscalYearOf(dateMax);
  const fyMonths = useMemo(() => fyMonthDates(fy), [fy]);
  const selectedMonths = useMemo(() => selectedMonthDates(fy, f.periodFrom, f.periodTo), [fy, f.periodFrom, f.periodTo]);
  const prevMonths = useMemo(() => new Set([...selectedMonths].map((mo) => monthShift(mo, -12))), [selectedMonths]);
  const rangeLabel = selectedRangeLabel(f.periodFrom, f.periodTo);
  const targetName = dept ? `🎯 ${dept}` : "🏥 病院全体";   // 入院実績と同じ表記

  // 前年度4月〜今年度3月 の 24 ヶ月を取得 (前年度線 + 前年比をクライアント計算するため)
  const fetchFrom = fyDate(fy - 1, 4);
  const fetchTo = fyDate(fy, 3);
  const basis = f.revenueBasis;   // [2026-07-18] 控除後の基準かどうか (材料・薬剤を差し引く)
  //   ★2026-08-22: 控除の源を 会計RP実請求額 → EF の包括前 に変更した (build_surgery_rp.R)。
  //     入院実績タブの 薬剤材料費差引後入院収入 と同じ源・同じ値になったので、表記もそれに揃える。
  const gross = basis === "粗利";
  const { data, isFetching } = useQuery<KbActResponse>({
    queryKey: ["kb_act", f.granularity, fetchFrom, fetchTo, iryo, yotei, basis],
    queryFn: () => api.kbAct({ granularity: f.granularity, dateFrom: fetchFrom, dateTo: fetchTo, iryo, yotei, basis }),
    placeholderData: (p) => p,
  });

  // データ実在月 (月平均の分母 = 実在月のみ。未来月・未確定月は除く)
  const availMonths = useMemo(() => new Set((data?.series ?? []).map((s) => s.month)), [data]);

  // 病院全体 (series) と 科別 (table) の 月×カテゴリ 生値
  const hospMonthCat = useMemo(() => {
    const m: MonthCat = {};
    for (const s of data?.series ?? []) {
      const c = catOf(s.行為区分);
      (m[s.month] ??= {})[c] = (m[s.month][c] ?? 0) + s.売上;
    }
    return m;
  }, [data]);
  const deptMonthCat = useMemo(() => {
    const m: Record<string, MonthCat> = {};
    for (const r of data?.table ?? []) {
      const c = catOf(r.行為区分);
      ((m[r.key] ??= {})[r.month] ??= {})[c] = (m[r.key][r.month][c] ?? 0) + r.売上;
    }
    return m;
  }, [data]);

  // 生値 (データ実在月のみ・無い月は null)
  const rawOf = (mc: MonthCat | undefined, mo: string, cs: string[]): number | null =>
    availMonths.has(mo) ? cs.reduce((a, c) => a + (mc?.[mo]?.[c] ?? 0), 0) : null;
  // 期間の月平均 (分母 = 期間内のデータ実在月)
  const periodAvg = (mc: MonthCat | undefined, cs: string[], months: Set<string>): number | null => {
    let sum = 0, n = 0;
    for (const mo of months) {
      const v = rawOf(mc, mo, cs);
      if (v == null) continue;
      sum += v; n += 1;
    }
    return n > 0 ? sum / n : null;
  };

  // チャート入力 (科フォーカス時はその科)
  const chartMC = dept ? deptMonthCat[dept] : hospMonthCat;
  // 棒グラフの月 = 直近13ヶ月 (前年同月〜最新確定月)。fetch 窓 (前年度4月〜) に必ず収まる。
  const chartMonths = useMemo(() => Array.from({ length: 13 }, (_, i) => monthShift(dateMax, i - 12)), [dateMax]);
  // 直近13ヶ月の x ラベル: 先頭と 4月・1月 に年を添える (年度枠の monthLabel は 4月/1月 だけ)
  const chartLabel = (mo: string, i: number) => {
    const mi = Number(mo.slice(5, 7));
    return i === 0 || mi === 4 || mi === 1 ? `${mo.slice(2, 4)}/${mi}月` : `${mi}月`;
  };
  const monthTotals = useMemo(
    () => chartMonths.map((mo) => rawOf(chartMC, mo, cats)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartMonths, chartMC, cats, availMonths],
  );
  const maxTotal = Math.max(1, ...monthTotals.map((v) => v ?? 0));

  // カード (期間の月平均 vs 前年度同期間の月平均)
  const cardDefs: { label: typeof metric; cats: string[] }[] = [
    { label: "入院収入", cats: CAT_ORDER },
    { label: "入院料", cats: ["入院料"] },
    { label: "手術", cats: ["手術"] },
    { label: "その他", cats: ["その他"] },
  ];
  const cardData = cardDefs.map((cd) => {
    const ser = fyMonths.map((mo) => ({ month: mo, v: rawOf(chartMC, mo, cd.cats), py: rawOf(chartMC, monthShift(mo, -12), cd.cats) }));
    return { ...cd, ser, cur: periodAvg(chartMC, cd.cats, selectedMonths), py: periodAvg(chartMC, cd.cats, prevMonths) };
  });

  // 科別表の行データ (値は表示指標)。年度内に売上のある科だけ。既定順 = 期間の月平均で降順。
  // 列ヘッダクリックで任意列の昇順/降順に切替 (useSort・値は行に持たせて stale クロージャ回避)。
  type DeptRow = { key: string; vals: (number | null)[]; avg: number | null };
  const deptRows = useMemo<DeptRow[]>(() => {
    const rows = Object.keys(deptMonthCat)
      .map((k) => ({
        key: k,
        vals: fyMonths.map((mo) => rawOf(deptMonthCat[k], mo, cats)),
        avg: periodAvg(deptMonthCat[k], cats, selectedMonths),
      }))
      .filter((r) => r.vals.some((v) => v != null && v !== 0));
    rows.sort((a, b) => (b.avg ?? -Infinity) - (a.avg ?? -Infinity));
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptMonthCat, fyMonths, cats, availMonths, selectedMonths]);
  const s = useSort(deptRows);
  const hospVals = fyMonths.map((mo) => rawOf(hospMonthCat, mo, cats));
  const hospAvg = periodAvg(hospMonthCat, cats, selectedMonths);

  // 期間の月の枠 (入院実績の月別/科別マトリックスと同じ流儀: 期間の先頭/末尾に太線)
  const selectedIndexes = fyMonths.map((mo, i) => selectedMonths.has(mo) ? i : -1).filter((i) => i >= 0);
  const selectedFirst = selectedIndexes[0] ?? -1;
  const selectedLast = selectedIndexes[selectedIndexes.length - 1] ?? -1;
  const posOf = (i: number): "first" | "middle" | "last" | "only" =>
    selectedFirst === selectedLast ? "only" : i === selectedFirst ? "first" : i === selectedLast ? "last" : "middle";
  const headFrame = (i: number) => {
    if (!selectedMonths.has(fyMonths[i])) return "";
    const pos = posOf(i);
    return [
      "border-t-2 border-t-slate-900",
      (pos === "first" || pos === "only") ? "border-l-2 border-l-slate-900" : "",
      (pos === "last" || pos === "only") ? "border-r-2 border-r-slate-900" : "",
    ].join(" ");
  };
  const bodyFrame = (i: number, isLastRow: boolean) => {
    if (!selectedMonths.has(fyMonths[i])) return "";
    const pos = posOf(i);
    return [
      (pos === "first" || pos === "only") ? "border-l-2 border-l-slate-900" : "",
      (pos === "last" || pos === "only") ? "border-r-2 border-r-slate-900" : "",
      isLastRow ? "border-b-2 border-b-slate-900" : "",
    ].join(" ");
  };

  const downloadCsv = () => {
    // 列順は画面の表と同じ (診療科 → 月平均 → 年度の各月)
    const head = ["診療科", `月平均(${rangeLabel})`, ...fyMonths.map((m) => m.slice(0, 7))];
    const cell = (v: number | null) => v == null ? "" : String(Math.round(v / 1000));
    const lines = [head.join(",")];
    lines.push(["病院全体", cell(hospAvg), ...hospVals.map(cell)].join(","));
    for (const r of s.rows) lines.push([`"${r.key}"`, cell(r.avg), ...r.vals.map(cell)].join(","));
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `行為別売上_${displayLabel(metric, gross)}${gross && metric !== "入院収入" ? "_薬剤材料費差引後" : ""}_科別月別_${fy}年度_${rangeLabel}.csv`; a.click();
  };

  return (
    <div>
      {/* 印刷 1 枚目: 条件ヘッダ (印刷専用) + タイトル行 + カード + 棒グラフ (.budget-print-page = 改ページ単位) */}
      <div className="budget-print-page kbact-print-page space-y-2">
      <ContextBar forPrint metrics={metrics} builtAt={builtAt} />
      <KbActHeader targetName={targetName} rangeLabel={rangeLabel} fy={fy}
        metricLabel={displayLabel(metric, gross)} basisLabel={gross ? "薬剤材料費差引後" : "入院収入"} />
      {/* ① 主要 4 指標カード (印刷では 1 行 4 枚 = .kbact-cards) */}
      <div className="kbact-cards grid grid-cols-2 xl:grid-cols-4 gap-3 mb-3">
        {cardData.map((cd) => (
          <KbActCard key={cd.label} label={cd.label} fy={fy} rangeLabel={rangeLabel} ser={cd.ser}
            cur={cd.cur} py={cd.py} gross={gross}
            active={metric === cd.label}
            onClick={() => f.setKbActMetric(cd.label)} />
        ))}
      </div>

      {/* ② 月次推移 縦積み上げ棒 (直近13ヶ月・x=年月・y=実額) */}
      <section className="kbact-chart-sec card p-3 mb-3">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h2 className="text-sm font-bold">💴 行為別売上 月次推移 <span className="text-[11px] font-normal text-slate-500">直近13ヶ月 (前年同月〜{ymShort(dateMax)})</span></h2>
          <span className="text-xs px-2 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-800 font-semibold">{displayLabel(metric, gross)}</span>
          <span className="text-xs px-2 py-0.5 rounded bg-violet-50 border border-violet-200 text-violet-800 font-semibold" title="サイドバーの 開始月/終了月 (入院実績と共通)">期間 {rangeLabel} の月平均</span>
          {/* [開発版(新GP) 2026-08-24] このタブは行為区分に医師人件費を帰属できないので、
              開発版でも GP ではない。「未控除」であることを明示する。 */}
          {gross && <span className="text-xs px-2 py-0.5 rounded bg-emerald-50 border border-emerald-300 text-emerald-800 font-semibold">{IS_THETA ? "薬剤材料費差引後 (医師人件費は未控除)" : "薬剤材料費差引後 (材料・薬剤を除く)"}</span>}
          {dept && (
            <button onClick={() => f.setFocusedDept(null)}
              className="text-xs px-2 py-0.5 rounded bg-amber-100 border border-amber-200 text-amber-800 hover:bg-amber-200 no-print"
              title="科フォーカスを解除">🎯 {dept} ✕</button>
          )}
          {dept && <span className="hidden print:inline text-xs px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800">🎯 {dept}</span>}
          {(iryo || yotei) && (
            <span className="text-[11px] px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800">
              {iryo ?? ""}{iryo && yotei ? "・" : ""}{yotei ?? ""}
            </span>
          )}
          {isFetching && <span className="text-[10px] text-slate-400 no-print">更新中…</span>}
          <span className="ml-auto text-[10px] text-slate-400">y=売上実額・総額は入院収入と整合 (自費・食事込み)・帯=期間の月</span>
        </div>

        {/* 背景クリック = 入院収入(総額) に戻す / セグメントクリック = そのカテゴリの指標へ */}
        <div className="kbact-chart flex items-end gap-[3px] h-56 border-b border-slate-300 pb-0 cursor-pointer"
          onClick={() => f.setKbActMetric("入院収入")}
          title="背景クリックで 入院収入(総額) に戻す">
          {chartMonths.map((mo, i) => {
            const tot = monthTotals[i];
            const selected = selectedMonths.has(mo);
            const colCls = `flex-1 min-w-0 flex flex-col items-center justify-end h-full rounded-t ${selected ? "bg-amber-50" : ""}`;
            if (tot == null) {
              return (
                <div key={mo} className={colCls} title={`${ymShort(mo)}: データなし`}>
                  <span className="text-[11px] text-slate-300 leading-none mb-0.5">—</span>
                </div>
              );
            }
            const hPct = (tot / maxTotal) * 100;
            return (
              <div key={mo} className={colCls}
                title={`${ymShort(mo)}: ${fmtAmt(tot)}円\n` + cats.map((c) => {
                  const v = rawOf(chartMC, mo, [c]) ?? 0;
                  return `${displayLabel(c)}: ${fmtAmt(v)}円 (${tot > 0 ? ((v / tot) * 100).toFixed(1) : 0}%)`;
                }).join("\n")}>
                <span className="text-[11px] font-semibold text-slate-700 leading-none mb-0.5 whitespace-nowrap">{fmtAmt(tot)}</span>
                <div className="w-full flex flex-col-reverse rounded-t overflow-hidden" style={{ height: `${hPct}%` }}>
                  {cats.map((c) => {
                    const v = rawOf(chartMC, mo, [c]) ?? 0;
                    if (v <= 0 || tot <= 0) return null;
                    const segPx = (v / maxTotal) * CHART_H;
                    const pct = (v / tot) * 100;
                    return (
                      <div key={c} style={{ height: `${(v / tot) * 100}%`, background: CAT_COLORS[c] }}
                        className="flex flex-col items-center justify-center text-white overflow-hidden whitespace-nowrap cursor-pointer hover:brightness-110"
                        onClick={(e) => { e.stopPropagation(); f.setKbActMetric(c as typeof f.kbActMetric); }}
                        title={`クリックで表示指標を「${displayLabel(c)}」に切替`}>
                        {segPx >= 16 && <span className="text-[10px] font-semibold leading-tight">{fmtAmt(v)}</span>}
                        {stacked && segPx >= 30 && <span className="text-[10px] leading-tight">{pct.toFixed(0)}%</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-[3px] mt-1">
          {chartMonths.map((mo, i) => (
            <div key={mo} className={`flex-1 min-w-0 text-center text-[11px] leading-tight whitespace-nowrap ${selectedMonths.has(mo) ? "text-slate-800 font-bold" : "text-slate-600"}`}>{chartLabel(mo, i)}</div>
          ))}
        </div>

        <div className="flex gap-3 text-[10px] text-slate-500 mt-2 flex-wrap">
          {cats.map((c) => <span key={c} className="flex items-center gap-1"><span style={{ width: 10, height: 10, background: CAT_COLORS[c], display: "inline-block", borderRadius: 2 }} />{displayLabel(c)}</span>)}
        </div>
      </section>
      </div>

      {/* 印刷 2 枚目: 条件ヘッダ (印刷専用) + 科別 × 年月 マトリクス */}
      <div className="budget-print-page kbact-print-page space-y-2">
      <ContextBar forPrint metrics={metrics} builtAt={builtAt} />
      {/* ③ 診療科 × 年月 マトリクス (印刷では 2 枚目 = .kbact-table-sec) */}
      <section className="kbact-table-sec card p-3">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h3 className="text-sm font-bold">科別 月別 {displayLabel(metric, gross)}（千円{gross && metric !== "入院収入" ? "・薬剤材料費差引後" : ""}・{fy}年度）</h3>
          <span className="text-[11px] text-slate-500">全診療科 × 年度 12 ヶ月・月平均 = 期間 {rangeLabel}{dept ? `・強調 = ${dept}` : ""}</span>
          <span className="text-[10px] text-slate-400 no-print">行クリックでその科を強調 (グラフも連動)・枠=期間の月</span>
          <button onClick={downloadCsv} className="no-print text-xs px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 ml-auto">CSV</button>
        </div>
        <div className="kbact-table-scroll overflow-auto" style={{ maxHeight: "62vh" }}>
          <table className="kbact-table text-[11px] border-collapse w-full">
            <thead className="sticky top-0 bg-slate-50 z-[1]">
              <tr>
                <SortTh s={s} sortKey="dept" get={(r) => (r as DeptRow).key}
                  className="px-2 py-1 text-left sticky left-0 bg-slate-50 border-b whitespace-nowrap">診療科</SortTh>
                <SortTh s={s} sortKey="avg" get={(r) => (r as DeptRow).avg ?? -Infinity}
                  className="px-2 py-1 text-right border-b font-bold whitespace-nowrap bg-slate-200 border-t-2 border-t-slate-900 border-l-2 border-l-slate-900 border-r-2 border-r-slate-900"
                  >月平均 ({rangeLabel})</SortTh>
                {fyMonths.map((m, i) => (
                  <SortTh key={m} s={s} sortKey={m} get={(r) => (r as DeptRow).vals[i] ?? -Infinity}
                    className={`px-2 py-1 text-right border-b whitespace-nowrap ${headFrame(i)}`}>{monthLabel(m)}</SortTh>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className={`font-semibold border-b-2 border-slate-300 cursor-pointer ${!dept ? "bg-slate-100/90" : "bg-slate-50 hover:bg-slate-100"}`}
                onClick={() => f.setFocusedDept(null)}
                title="クリックで病院全体のグラフに戻す">
                <td className="px-2 py-1 text-left sticky left-0 bg-slate-100 whitespace-nowrap">{!dept && "🎯 "}病院全体</td>
                <td className={`px-2 py-1 text-right font-bold tabular-nums border-l-2 border-l-slate-900 border-r-2 border-r-slate-900 ${s.rows.length === 0 ? "border-b-2 border-b-slate-900" : ""}`}>{sen(hospAvg)}</td>
                {hospVals.map((v, i) => <td key={fyMonths[i]} className={`px-2 py-1 text-right tabular-nums ${bodyFrame(i, s.rows.length === 0)}`}>{sen(v)}</td>)}
              </tr>
              {s.rows.map((r, ri) => {
                const focused = dept === r.key;
                const isLastRow = ri === s.rows.length - 1;
                return (
                  <tr key={r.key}
                    className={`cursor-pointer ${focused ? "bg-amber-100 outline outline-2 outline-amber-400 font-semibold" : "hover:bg-blue-50"}`}
                    onClick={() => f.setFocusedDept(focused ? null : r.key)}
                    title={focused ? "クリックで解除" : "クリックでこの科を強調 (グラフも連動)"}>
                    <td className={`px-2 py-1 text-left sticky left-0 border-b whitespace-nowrap ${focused ? "bg-amber-100" : "bg-white"}`}>{focused && "🎯 "}{r.key}</td>
                    <td className={`px-2 py-1 text-right border-b font-bold tabular-nums border-l-2 border-l-slate-900 border-r-2 border-r-slate-900 ${isLastRow ? "border-b-2 border-b-slate-900" : ""}`}>{sen(r.avg)}</td>
                    {r.vals.map((v, i) => (
                      <td key={fyMonths[i]} className={`px-2 py-1 text-right border-b tabular-nums ${bodyFrame(i, isLastRow)}`}>{v == null ? "—" : v ? sen(v) : "—"}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-400 mt-2">
          ※診療科は素分類 (回復期_ を除いた元の科)。科別の金額も入院実績タブの入院収入と一致する。
          入院料 = 入院基本料 (kouseicd A1/A4 始まり・短期滞在手術等基本料3 を含む) + 特定入院料 (A3 始まり)。
          手術(輸血除く) = 手術/手術材料/手術薬剤 (輸血・麻酔・初再診・在宅・労災はその他)。
          月平均 = 期間 ({rangeLabel}) のうちデータのある月の平均。既定の行順=月平均の降順 (列ヘッダで切替)。
          {gross && " 薬剤材料費差引後 = 材料・薬剤 (EF の包括前・全行為区分) を控除した額。入院実績タブの「薬剤材料費差引後入院収入」と同じ源。"}
        </p>
      </section>
      </div>
    </div>
  );
}

// タイトル行 (画面・印刷とも表示。入院実績の Header と同じ流儀): 対象 + 期間の月平均 + 年度/表示指標/収入の基準 + 3 区分の凡例
function KbActHeader({ targetName, rangeLabel, fy, metricLabel, basisLabel }: {
  targetName: string; rangeLabel: string; fy: number; metricLabel: string; basisLabel: string;
}) {
  return (
    <div className="kbact-header flex items-center gap-3 flex-wrap">
      <h2 className="text-xl font-bold">{targetName} 行為別売上({rangeLabel}の月平均)</h2>
      <span className="text-xs text-slate-500">
        {fy}年度・表示指標 <b className="text-slate-700">{metricLabel}</b>・収入の基準 <b className="text-slate-700">{basisLabel}</b>
      </span>
      <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
        {CAT_ORDER.map((c) => (
          <span key={c} className="flex items-center gap-1">
            <span style={{ width: 12, height: 12, background: CAT_COLORS[c], display: "inline-block", borderRadius: 2 }} />{displayLabel(c)}
          </span>
        ))}
      </div>
    </div>
  );
}

// 主要指標カード (旧 行為別タブの KbCard 流儀): 期間の月平均 (単位自動) + 前年度同期間・前年比 +
// 年度12ヶ月の 今年度(緑)/前年度(グレー) 折れ線 (x軸は 入院実績 と同じ 4月始まり・月数字のみ)。クリックで表示指標切替。
function KbActCard({ label, fy, rangeLabel, ser, cur, py, gross, active, onClick }: {
  label: string; fy: number; rangeLabel: string;
  ser: { month: string; v: number | null; py: number | null }[];
  cur: number | null; py: number | null;
  // [2026-08-22] 収入の基準が控除後か。総額カードの見出しを「薬剤材料費差引後入院収入」にする
  gross: boolean;
  active: boolean; onClick: () => void;
}) {
  const yoy = (cur != null && py != null && py !== 0) ? cur / py : null;
  const yoyColor = yoy == null ? "text-slate-400" : yoy >= 1 ? "text-blue-700" : "text-red-600";
  const sig = `${fy}|` + ser.map((s) => `${s.v}/${s.py}`).join(",");
  const opts = useMemo<Plot.PlotOptions>(() => {
    const rows: { date: Date; value: number; s: "今年度" | "前年度" }[] = [];
    for (const w of ser) {
      if (w.v != null) rows.push({ date: new Date(w.month), value: w.v, s: "今年度" });
      if (w.py != null && w.py > 0) rows.push({ date: new Date(w.month), value: w.py, s: "前年度" });
    }
    // x 軸 = 年度 12 ヶ月 (4月〜3月) 固定・月ラベルは 入院実績(前年度・予算比較) と同じ buildMonthAxisMark 流儀
    // (毎月 tick・月数字のみ・fontSize 11 → marginBottom 24 必須)
    const xDomain: [Date, Date] = [new Date(fyDate(fy, 4)), new Date(fyDate(fy, 3))];
    return {
      width: 420, height: 124, marginLeft: 48, marginRight: 8, marginTop: 8, marginBottom: 24,
      style: { fontFamily: "var(--jp-font, system-ui)", fontSize: "9px" },
      x: { label: null, type: "time", axis: null, domain: xDomain },
      y: { label: null, grid: true, nice: true, zero: false, tickFormat: (v: number) => fmtAmt(v) },
      marks: [
        Plot.lineY(rows.filter((d) => d.s === "前年度"), { x: "date", y: "value", stroke: "#94a3b8", strokeWidth: 2, strokeOpacity: 0.6 }),
        Plot.lineY(rows.filter((d) => d.s === "今年度"), { x: "date", y: "value", stroke: "#1B9E77", strokeWidth: 2.5 }),
        Plot.dot(rows.filter((d) => d.s === "今年度"), { x: "date", y: "value", fill: "#1B9E77", r: 2.2 }),
        buildMonthAxisMark(xDomain),
      ],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);
  return (
    <button type="button" onClick={onClick}
      className={`kbact-card border rounded-lg p-3 bg-white shadow-sm text-left transition-shadow ${active ? "ring-2 ring-blue-400 border-blue-300" : "hover:shadow"}`}
      title={`クリックで表示指標を「${displayLabel(label, gross)}」に切替`}>
      <div className="text-sm font-bold text-slate-700 flex items-center gap-1">
        <span className="min-w-0 truncate">{displayLabel(label, gross)}</span>
        <span className="text-[10px] text-slate-400 font-normal ml-auto whitespace-nowrap">{rangeLabel} 月平均</span>
      </div>
      <div className="mt-0.5 text-2xl font-extrabold text-slate-900 leading-none">
        {cur != null ? fmtAmt(cur) : "—"}<span className="text-slate-400 font-normal text-sm">円</span>
      </div>
      <div className="mt-1 text-xs flex items-baseline gap-2 flex-wrap">
        <span className="text-slate-500">前年度同期間 {py != null && py > 0 ? `${fmtAmt(py)}円` : "—"}</span>
        <span className={`font-bold ${yoyColor}`}>前年比 {yoy != null ? `${(yoy * 100).toFixed(1)}%` : "—"}</span>
      </div>
      <div className="mt-1"><PlotFigure options={opts} responsiveWidth /></div>
      <div className="mt-0.5 flex items-center gap-3 text-[10px] text-slate-500">
        <span className="flex items-center gap-1"><span style={{ width: 14, height: 3, background: "#1B9E77", display: "inline-block" }} />今年度</span>
        <span className="flex items-center gap-1"><span style={{ width: 14, height: 2, background: "#94a3b8", opacity: 0.7, display: "inline-block" }} />前年度</span>
      </div>
    </button>
  );
}
